import React from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import {
  Icon,
  InPlaceEditingPlaceholder,
  ObjValidator,
  XCircleLoader,
} from "components";
import { isBrowserEnv } from "utils/isWindowObjAvailable";
import { getHomepage, isWirelessTenantAllowed } from "utils";
import { AddWizardToProject } from "components/_Modals/AddWizardToProject";
import { useTranslate } from "hooks";
import { useAuthStore } from "stores/authStore";
import s from "./WizardList.module.scss";
import { isProduction } from "utils/environment";
import { FeatureUnavailable } from "components/FeatureUnavailable";
import { useEnabledWizardObjs } from "hooks/wizardHooks/useEnabledWizardObjs";

const WizardList = Scrivito.connect(() => {
  const translate = useTranslate();
  const user = useAuthStore.use.currentUser();
  const { projectId } = Scrivito.currentPageParams();
  const { wizardObjs, isLoading } = useEnabledWizardObjs();
  const featureFlags = useAuthStore.use.featureFlags();

  const configWizardObjRefs = getHomepage().get("configWizardObjRefs");
  const wizardObjRef = configWizardObjRefs.find(
    (item) => item.objClass() === "Wizard",
  );

  if (isLoading) {
    return <XCircleLoader />;
  }

  if (!featureFlags["WIZARDS"]) {
    return <FeatureUnavailable />;
  }

  return (
    <div className="pt-4 bg-gray-100 flex-grow-1">
      <div className={s.cardListContainer}>
        <h3 className="text-uppercase mb-1">
          {translate("WIZARDS_OVERVIEW_TITLE")}
        </h3>
        <h4 className="mb-4">{translate("WIZARDS_OVERVIEW_SUBTITLE")}</h4>
        <div className="w-100 pb-4">
          <div className={s.cardList}>
            {wizardObjs.map((wizardItem) => {
              const isProdReady = wizardItem.get("isProdReady");
              if (!isProdReady && isProduction) {
                return null;
              }
              const subType = wizardItem.get("subType");
              const image = wizardItem.get("tileImage");
              if (
                subType === "roomcontrolwireless" &&
                !isWirelessTenantAllowed(user?.tenant)
              ) {
                return null;
              }
              const linkForMenuItem = wizardItem.get("linkForMenuItem");
              const title = translate(
                `WIZARDS_OVERVIEW_${subType?.toUpperCase()}_TITLE`,
              );
              const subtitle = translate(
                `WIZARDS_OVERVIEW_${subType?.toUpperCase()}_SUBTITLE`,
              );
              const description = translate(
                `WIZARDS_OVERVIEW_${subType?.toUpperCase()}_DESCRIPTION`,
              );
              return (
                <div
                  key={subType}
                  className={cx(
                    "position-relative d-flex flex-column flex-grow-1 p-3 rounded-1 bg-white shadow-sm cursor-pointer",
                    s.card,
                  )}
                  onClick={() => {
                    if (linkForMenuItem && isBrowserEnv()) {
                      window.open(linkForMenuItem.url(), "_blank").focus();
                    } else if (!projectId) {
                      NiceModal.show(AddWizardToProject, { wizardItem });
                    } else {
                      Scrivito.navigateTo(
                        () => wizardObjRef || Scrivito.currentPage(),
                        {
                          params: {
                            subType,
                            projectId,
                            step: "0",
                          },
                        },
                      );
                    }
                  }}
                >
                  <div
                    className={cx(
                      "d-flex justify-content-between align-items-center w-100 h-100 p-2",
                      s.cardImgWrapper,
                    )}
                  >
                    {image ? (
                      <Scrivito.BackgroundImageTag
                        className={cx("w-100 h-100", s.cardImg)}
                        style={{
                          background: { image, size: "contain" },
                        }}
                      />
                    ) : (
                      <div className="d-flex flex-column justify-content-center align-items-center w-100 h-100 font24 text-white fw-bold">
                        <Icon
                          iconName="image-slash"
                          className="icon-40 mb-3 text-gray-400"
                        />
                        <InPlaceEditingPlaceholder>
                          {translate("ERROR_NO_IMAGE")}
                        </InPlaceEditingPlaceholder>
                      </div>
                    )}
                  </div>
                  <div className="opacity-0 me-3">
                    <h3 className="mb-1">{title}</h3>
                    <h4>{subtitle}</h4>
                  </div>
                  <div
                    className={cx(
                      "position-absolute w-100 bottom-0 start-0 p-3 bg-white",
                    )}
                  >
                    <div className="d-flex justify-content-between align-items-center w-100 mb-2">
                      <div>
                        <h3 className="d-flex align-self-start mb-1 text-start">
                          {title}
                        </h3>
                        <h4 className="d-flex align-self-start text-start mb-0">
                          {subtitle}
                        </h4>
                      </div>
                      <Icon
                        className={cx("icon-24", s.iconArrow)}
                        prefix="trox"
                        iconName="chevron-right-regular"
                      />
                    </div>
                    <div className={s.cardDesc}>{description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
});

const CheckedPage = (props) => {
  const isPageEnabled = useAuthStore.use.featureFlags("WIZARDS");
  if (!isPageEnabled) {
    return <FeatureUnavailable />;
  }
  return (
    <ObjValidator {...props}>
      <WizardList {...props} />
    </ObjValidator>
  );
};
export default CheckedPage;
Scrivito.provideComponent("WizardList", CheckedPage);
